/**
 * The shared names of Music24 Outreach ("Pitch"). music24-api, the web app, the admin and the
 * native apps use the same strings. The Prisma columns of music24-api store these strings.
 */

export type OutreachTargetKind =
  | 'playlist'
  | 'press'
  | 'radio'
  | 'newsletter'
  | 'podcast'
  | 'creator'
  /** A Reddit promotion subreddit. A reference entry, never a send target: it has no email. */
  | 'community';
export type OutreachContactKind = 'curator' | 'outlet' | 'creator' | 'private';
export type OutreachPointType =
  | 'email'
  | 'instagram'
  | 'tiktok'
  | 'youtube'
  | 'website'
  | 'form'
  | 'submithub'
  | 'groover'
  | 'soundcloud'
  | 'bandcamp'
  | 'linkInBio';
export type OutreachPointSource =
  | 'playlistDescription'
  | 'website'
  | 'linkInBio'
  | 'instagramPublic'
  | 'youtubeAbout'
  | 'soundcloud'
  | 'bandcamp'
  /** Legacy: the removed outlet web search (owner decision 2026-10-12). Old points keep it. */
  | 'webSearch'
  /** A Substack or beehiiv public newsletter page. */
  | 'newsletterIndex'
  /** An Apple Podcasts or Podchaser public show page. */
  | 'podcastIndex'
  /** A podcast RSS feed (`itunes:owner` email, `managingEditor`, `webMaster`, `channel > link`). */
  | 'podcastFeed'
  /** A public TikTok profile, read logged out. */
  | 'tiktokPublic'
  /** A Reddit public `about.json` / `about/rules.json` read (a `community` target). */
  | 'redditAbout'
  | 'import'
  | 'admin';
export type OutreachCampaignStatus = 'draft' | 'writing' | 'review' | 'scheduled' | 'sending' | 'paused' | 'done';
export type OutreachCampaignMode = 'review' | 'autopilot';
export type OutreachMessageStatus =
  | 'draft'
  | 'needsEdit'
  | 'approved'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'bounced'
  | 'replied'
  | 'skipped'
  | 'failed'
  | 'blocked';
export type OutreachReplyLabel =
  | 'interested'
  | 'placed'
  | 'notAFit'
  | 'asksForPayment'
  | 'later'
  | 'outOfOffice'
  | 'unsubscribe'
  | 'bounce'
  | 'other';
/** What a curator asks for in a reply (P6 `asks`). */
export type OutreachReplyAsk = 'link' | 'releaseDate' | 'wav' | 'pressPhoto' | 'lyrics' | 'story' | 'other';
export const OUTREACH_REPLY_ASKS: readonly OutreachReplyAsk[] = [
  'link',
  'releaseDate',
  'wav',
  'pressPhoto',
  'lyrics',
  'story',
  'other',
];
/**
 * The inbox groups (design m1): `needsYou` (interested, question, later, payment until archived),
 * `added` (a placement), `done` (not a fit, answered, archived), `hidden` (out of office, bounce,
 * unsubscribe: Plug acts on them).
 */
export type OutreachInboxGroup = 'needsYou' | 'added' | 'done' | 'hidden';
export const OUTREACH_INBOX_GROUPS: readonly OutreachInboxGroup[] = ['needsYou', 'added', 'done', 'hidden'];
/** A link that an inbox answer can carry (design m1 attachment chips). Never a file in the mail. */
export type OutreachAttachmentKind = 'spotifyLink' | 'presaveLink' | 'masterDownload' | 'pressPhoto' | 'lyrics';
/** `imapSmtp` is the only provider: the artist connects an own mailbox (Outreach was never released). */
export type OutreachMailboxProvider = 'imapSmtp';
export type OutreachMailboxHealth = 'ok' | 'warming' | 'paused' | 'reconnect' | 'provisioning';
/**
 * Who paused a mailbox: the owner, the health gate, or Music24 support. The owner cannot resume a
 * `support` pause (owner decision 2026-10-12); only the admin resume does.
 */
export type OutreachMailboxPauseCause = 'owner' | 'health' | 'support';
export type OutreachSuppressionReason = 'unsubscribe' | 'bounce' | 'complaint' | 'curatorRequest' | 'admin';
export type OutreachWorkspaceContactStatus = 'new' | 'pitched' | 'replied' | 'placed' | 'doNotContact';
export type OutreachBlockReason =
  | 'suppressed'
  | 'fatigue'
  | 'alreadyPitchedRelease'
  | 'coolDown'
  | 'noEmail'
  | 'genreMismatch'
  | 'paidPlacementFlag'
  /**
   * A second pitch to a contact (an other release) costs one pitch credit, and the workspace has
   * none. The message waits; a pitch pack or the next month frees it. Only `sendGuard` sets it.
   */
  | 'noPitchCredit'
  /**
   * The recipient chose "Fewer pitches" with a genre list on the stop page, and the genres of the
   * release do not meet it.
   */
  | 'recipientGenres';
export type OutreachPlacementSource = 'detected' | 'reply' | 'manual';
export type OutreachCareerTier = 'emerging' | 'developing' | 'mid' | 'established';

/** The value lists, for zod enums and admin filters. Same order as the types. */
export const OUTREACH_TARGET_KINDS: readonly OutreachTargetKind[] = [
  'playlist',
  'press',
  'radio',
  'newsletter',
  'podcast',
  'creator',
  'community',
];
export const OUTREACH_CONTACT_KINDS: readonly OutreachContactKind[] = ['curator', 'outlet', 'creator', 'private'];

/**
 * The facts a `community` target stores in `OutreachTargetModel.rules.community`. A subreddit is a
 * reference entry that tells an artist where they may post their own song, with the subreddit's own
 * rules. It is never a send target: it has no email.
 */
export interface OutreachCommunityRules {
  platform: 'reddit';
  /** The subreddit, with the `r/` prefix (`r/listentothis`). */
  handle: string;
  /** The subscriber count that the public `about.json` shows. */
  members: number;
  /** A short summary of the posting rules, from the public `about/rules.json`. */
  postingRules: string;
  /** True when the rules let a creator post their own music. */
  allowsSelfPromo: boolean;
  /** The weekly thread or day for self-promotion, when the rules name one. */
  dayOrThread?: string;
}
export const OUTREACH_REPLY_LABELS: readonly OutreachReplyLabel[] = [
  'interested',
  'placed',
  'notAFit',
  'asksForPayment',
  'later',
  'outOfOffice',
  'unsubscribe',
  'bounce',
  'other',
];
